import { useState } from "react";
import { Board } from "@design-system/Board/Board";
import type { BoardMove } from "@design-system/Board/Board.types";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import {
    TagOverflow,
    type TagOverflowItem,
} from "@design-system/Tag/TagOverflow";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./TagOverflowPlayground.module.scss";

interface DemoCard {
    id: string;
    title: string;
    columnId: string;
}

const columns = [
    { id: "todo", title: "To do" },
    { id: "done", title: "Done" },
];

const initialCards: readonly DemoCard[] = [
    { id: "payroll", title: "Run October payroll", columnId: "todo" },
    { id: "close", title: "Close Q3 books", columnId: "todo" },
];

const getCardId = (card: DemoCard) => card.id;
const getCardTitle = (card: DemoCard) => card.title;
const getPlacements = (card: DemoCard) => [
    { columnId: card.columnId, laneId: null },
];

/**
 * A draggable two-column board whose cards carry a `TagOverflow`: clicking
 * the +N tag or a tag in its popover must not open or drag the card.
 */
export const TagOverflowPlaygroundBoard = ({
    items,
    max,
}: Readonly<{ items: readonly TagOverflowItem[]; max: number }>) => {
    const [cards, setCards] = useState(initialCards);
    const [openedTitles, setOpenedTitles] = useState<string[]>([]);

    const moveCard = ({ item, to }: BoardMove<DemoCard>) =>
        setCards((current) =>
            current.map((card) =>
                card.id === item.id ? { ...card, columnId: to.columnId } : card,
            ),
        );

    return (
        <VStack gap={8} className={styles.boardDemo}>
            <div className={styles.board}>
                <Board
                    aria-label="Tag overflow demo"
                    items={cards}
                    columns={columns}
                    getItemId={getCardId}
                    getItemLabel={getCardTitle}
                    getPlacements={getPlacements}
                    onMove={moveCard}
                    renderCard={(card) => (
                        <BoardCard
                            onClick={() =>
                                setOpenedTitles((titles) => [
                                    ...titles,
                                    card.title,
                                ])
                            }
                        >
                            <BoardCardLayout
                                title={card.title}
                                fields={[
                                    {
                                        id: "tags",
                                        placement: "stacked",
                                        content: (
                                            <TagOverflow
                                                items={items}
                                                max={max}
                                                triggerTabIndex={-1}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </BoardCard>
                    )}
                />
            </div>
            <code>
                Opened:{" "}
                {openedTitles.length > 0 ? openedTitles.join(", ") : "none"}
            </code>
        </VStack>
    );
};
